import React, { useCallback, useEffect, useRef, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeDashboardDialog } from '@actions/dashboard-actions';
import { setMapFilters } from '@actions/map-actions';
import * as dialog from '@constants/dialogs';
import BaseDateRangeDialog, { useDateRangeDialog } from '@shared/dialogs/base-date-range-dialog';
import { decodeDateFilters, generateDateFilters } from '@utils/date-utils';

/**
 * Map date range dialog component.
 * @returns {JSX.Element|null} The rendered dialog or null.
 */
const MapDateRangeDialog = () => {
  const dispatch = useDispatch();

  const activeDialogs = useSelector(state => state.dashboard.activeDialogs);
  const mapFilters = useSelector(state => state.map.filters);
  const { start: startProp, end: endProp, contain: containProp } = decodeDateFilters(mapFilters);

  const {
    start,
    end,
    contain,
    resetToEmpty,
    handleStartChange,
    handleEndChange
  } = useDateRangeDialog({
    start: startProp,
    end: endProp,
    contain: containProp,
    useGlobalDefaults: false
  });

  // Reset to empty when dialog opens
  const prevOpenRef = useRef(activeDialogs[dialog.DATE_RANGE]);
  useEffect(() => {
    const isOpen = activeDialogs[dialog.DATE_RANGE];
    const wasOpen = prevOpenRef.current;
    if (!wasOpen && isOpen) {
      resetToEmpty();
    }
    prevOpenRef.current = isOpen;
  }, [activeDialogs, resetToEmpty]);

  const getFilters = useCallback(
    () => generateDateFilters({ start, end, contain }),
    [start, end, contain]
  );

  const closeDialog = useCallback(() => {
    dispatch(closeDashboardDialog(dialog.DATE_RANGE));
  }, [dispatch]);

  const handleCancel = useCallback(() => {
    resetToEmpty();
    closeDialog();
  }, [resetToEmpty, closeDialog]);

  const handleApply = useCallback(() => {
    dispatch(setMapFilters(getFilters()));
    closeDialog();
  }, [dispatch, getFilters, closeDialog]);

  return (
    <BaseDateRangeDialog
      isOpen={activeDialogs[dialog.DATE_RANGE]}
      start={start}
      end={end}
      contain={contain}
      handleStartChange={handleStartChange}
      handleEndChange={handleEndChange}
      onCancel={handleCancel}
      onApply={handleApply}
      showContain={false}
    />
  );
};

export default memo(MapDateRangeDialog);
